import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const EmployersList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sizeFilter, setSizeFilter] = useState('all');

  // Mock data - replace with actual API call
  const employers = [
    {
      id: 'EMP-001',
      companyName: 'TechCorp Inc.',
      email: 'contact@techcorp.com',
      phone: '+1 (555) 123-4567',
      location: 'New York, NY, USA',
      industry: 'Technology',
      employeeSize: '100-200',
      status: 'active',
      jobsPosted: 15,
      activeJobs: 8,
      totalApplications: 234,
      verified: true,
      joinDate: '2023-06-15',
      lastActive: '2024-01-20',
      logo: '/api/placeholder/40/40'
    },
    {
      id: 'EMP-002',
      companyName: 'StartUp Labs',
      email: 'hr@startuplabs.com',
      phone: '+1 (555) 987-6543',
      location: 'San Francisco, CA, USA',
      industry: 'SaaS',
      employeeSize: '20-50',
      status: 'active',
      jobsPosted: 8,
      activeJobs: 5,
      totalApplications: 89,
      verified: false,
      joinDate: '2023-09-10',
      lastActive: '2024-01-18',
      logo: '/api/placeholder/40/40'
    },
    {
      id: 'EMP-003',
      companyName: 'Global Solutions Ltd.',
      email: 'careers@globalsolutions.com',
      phone: '+1 (555) 456-7890',
      location: 'Chicago, IL, USA',
      industry: 'Consulting',
      employeeSize: '500-1000',
      status: 'active',
      jobsPosted: 25,
      activeJobs: 12,
      totalApplications: 567,
      verified: true,
      joinDate: '2023-03-22',
      lastActive: '2024-01-19',
      logo: '/api/placeholder/40/40'
    },
    {
      id: 'EMP-004',
      companyName: 'Innovate Tech',
      email: 'info@innovatetech.com',
      phone: '+1 (555) 234-5678',
      location: 'Austin, TX, USA',
      industry: 'Software Development',
      employeeSize: '50-100',
      status: 'inactive',
      jobsPosted: 6,
      activeJobs: 0,
      totalApplications: 45,
      verified: true,
      joinDate: '2023-11-05',
      lastActive: '2024-01-10',
      logo: '/api/placeholder/40/40'
    },
    {
      id: 'EMP-005',
      companyName: 'Digital Wave',
      email: 'hello@digitalwave.com',
      phone: '+1 (555) 345-6789',
      location: 'Boston, MA, USA',
      industry: 'Digital Marketing',
      employeeSize: '1-20',
      status: 'active',
      jobsPosted: 4,
      activeJobs: 3,
      totalApplications: 67,
      verified: false,
      joinDate: '2024-01-08',
      lastActive: '2024-01-20',
      logo: '/api/placeholder/40/40'
    }
  ];

  const employeeSizes = ['1-20', '20-50', '50-100', '100-200', '200-500', '500-1000', '1000-5000'];

  const filteredEmployers = employers.filter(employer => {
    const matchesSearch = employer.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employer.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || employer.status === statusFilter;
    const matchesSize = sizeFilter === 'all' || employer.employeeSize === sizeFilter;
    
    return matchesSearch && matchesStatus && matchesSize;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' }
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const getVerificationBadge = (verified) => {
    return verified ? (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <i className="fas fa-check-circle mr-1"></i> Verified
      </span>
    ) : (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <i className="fas fa-clock mr-1"></i> Pending
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Employers Management</h2>
            <p className="text-gray-400">Manage employer accounts and companies</p>
          </div>
          <Link
            to="/hpanel/employers/add"
            className="btn-primary text-white font-semibold py-2 px-4 rounded-lg flex items-center"
          >
            <i className="fas fa-plus mr-2"></i>
            Add Employer
          </Link>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search employers by company, email, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-gray-400"></i>
              </div>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Size Filter */}
          <div>
            <select
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Sizes</option>
              {employeeSizes.map(size => (
                <option key={size} value={size}>{size} employees</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Employers Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status & Verification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jobs & Applications
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEmployers.map((employer) => (
                <tr key={employer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                          {employer.companyName.charAt(0)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {employer.companyName}
                        </div>
                        <div className="text-sm text-gray-500">{employer.industry}</div>
                        <div className="text-xs text-gray-400">{employer.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{employer.email}</div>
                    <div className="text-sm text-gray-500">{employer.phone}</div>
                    <div className="text-sm text-gray-500">{employer.location}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      {getStatusBadge(employer.status)}
                      {getVerificationBadge(employer.verified)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employer.employeeSize} employees
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 space-y-1">
                      <div className="flex justify-between">
                        <span>Jobs Posted:</span>
                        <span className="font-medium">{employer.jobsPosted}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Active Jobs:</span>
                        <span className="font-medium text-green-600">{employer.activeJobs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Applications:</span>
                        <span className="font-medium text-blue-600">{employer.totalApplications}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(employer.lastActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <Link
                        to={`/hpanel/employers/${employer.id}`}
                        className="text-primary-600 hover:text-primary-900"
                        title="View"
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/hpanel/employers/edit/${employer.id}`}
                        className="text-green-600 hover:text-green-900"
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button className="text-red-600 hover:text-red-900" title="Delete">
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredEmployers.length === 0 && (
          <div className="text-center py-12">
            <i className="fas fa-building text-4xl text-gray-300 mb-4"></i>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No employers found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria or add a new employer.</p>
            <Link
              to="/hpanel/employers/add"
              className="btn-primary text-white font-semibold py-2 px-6 rounded-lg inline-flex items-center"
            >
              <i className="fas fa-plus mr-2"></i>
              Add Employer
            </Link>
          </div>
        )}

        {/* Pagination */}
        {filteredEmployers.length > 0 && (
          <div className="bg-white px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredEmployers.length}</span> of{' '}
                <span className="font-medium">{filteredEmployers.length}</span> results
              </div>
              <div className="flex space-x-2">
                <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Previous
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployersList;